import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Building2, Edit, FileText, Phone, Mail, MapPin, Star, Clock, TrendingUp } from 'lucide-react';
import { useSupplierDocuments, useSupplierPriceHistory } from '../../../api/queries/purchasing.queries';
import type { Supplier } from '../../../api/queries/purchasing.queries';

interface SupplierDetailModalProps {
  supplier: Supplier;
  onClose: () => void;
  onEdit: () => void;
}

type TabType = 'info' | 'documents' | 'prices';

export default function SupplierDetailModal({ supplier, onClose, onEdit }: SupplierDetailModalProps) {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabType>('info');

  const { data: documents } = useSupplierDocuments(supplier._id);
  const { data: priceHistory } = useSupplierPriceHistory(supplier._id);

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString(i18n.language === 'th' ? 'th-TH' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      onKeyDown={handleKeyDown}
    >
      <div className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-lg bg-card flex flex-col">
        {/* Header */}
        <div className="border-b p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Building2 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold">{supplier.name}</h2>
              {supplier.code && (
                <p className="text-sm text-muted-foreground">{supplier.code}</p>
              )}
            </div>
            {supplier.isPreferred && (
              <span className="px-2 py-1 rounded bg-primary/10 text-primary text-xs font-medium flex items-center gap-1">
                <Star className="h-3 w-3" />
                {t('purchasing.preferred')}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onEdit}
              className="p-2 hover:bg-secondary rounded flex items-center gap-1 text-sm"
            >
              <Edit className="h-4 w-4" />
              {t('common.edit')}
            </button>
            <button onClick={onClose} className="p-2 hover:bg-secondary rounded">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b px-4">
          <div className="flex gap-4">
            {(['info', 'documents', 'prices'] as TabType[]).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-3 border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-primary text-primary font-medium'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {t(`purchasing.${tab}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'info' && (
            <div className="space-y-6">
              {/* Performance Metrics */}
              {supplier.performanceMetrics && (
                <div className="grid grid-cols-4 gap-4">
                  <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                    <div className="text-sm text-green-600">{t('purchasing.onTimeRate')}</div>
                    <div className="text-2xl font-bold text-green-700">
                      {(supplier.performanceMetrics.onTimeDeliveryRate * 100).toFixed(0)}%
                    </div>
                  </div>
                  <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                    <div className="text-sm text-blue-600">{t('purchasing.qualityRating')}</div>
                    <div className="text-2xl font-bold text-blue-700">
                      {supplier.performanceMetrics.qualityRating.toFixed(1)}/5
                    </div>
                  </div>
                  <div className="p-4 rounded-lg bg-purple-50 border border-purple-200">
                    <div className="text-sm text-purple-600">{t('purchasing.totalOrders')}</div>
                    <div className="text-2xl font-bold text-purple-700">
                      {supplier.performanceMetrics.totalOrders}
                    </div>
                  </div>
                  <div className="p-4 rounded-lg bg-orange-50 border border-orange-200">
                    <div className="text-sm text-orange-600">{t('purchasing.avgOrderValue')}</div>
                    <div className="text-2xl font-bold text-orange-700">
                      {formatCurrency(supplier.performanceMetrics.averageOrderValue)}
                    </div>
                  </div>
                </div>
              )}

              {/* Contact Info */}
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-3">
                  {t('purchasing.contactInfo')}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{supplier.phone}</span>
                  </div>
                  {supplier.alternatePhone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span>{supplier.alternatePhone}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span>{supplier.email}</span>
                  </div>
                  {supplier.website && (
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-muted-foreground" />
                      <a href={supplier.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        {supplier.website}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Address */}
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-3">
                  {t('purchasing.address')}
                </h3>
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p>{supplier.address}</p>
                    {supplier.city && <p>{supplier.city}, {supplier.province} {supplier.postalCode}</p>}
                    <p>{supplier.country}</p>
                  </div>
                </div>
              </div>

              {/* Business Terms */}
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-3">
                  {t('purchasing.businessTerms')}
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span>{t('purchasing.leadTime')}: {supplier.leadTimeDays} {t('common.days')}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">{t('purchasing.paymentTerms')}:</span>{' '}
                    {supplier.paymentTermsDays} {t('common.days')}
                  </div>
                  {supplier.minimumOrderValue && (
                    <div>
                      <span className="text-muted-foreground">{t('purchasing.minOrder')}:</span>{' '}
                      {formatCurrency(supplier.minimumOrderValue)}
                    </div>
                  )}
                  {supplier.creditLimit && (
                    <div>
                      <span className="text-muted-foreground">{t('purchasing.creditLimit')}:</span>{' '}
                      {formatCurrency(supplier.creditLimit)}
                    </div>
                  )}
                </div>
              </div>

              {/* Bank Account */}
              {supplier.bankAccount && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-3">
                    {t('purchasing.bankAccount')}
                  </h3>
                  <div className="p-4 rounded-lg bg-secondary/50 text-sm space-y-1">
                    {supplier.bankAccount.bankName && <p><span className="text-muted-foreground">{t('purchasing.bankName')}:</span> {supplier.bankAccount.bankName}</p>}
                    {supplier.bankAccount.accountName && <p><span className="text-muted-foreground">{t('purchasing.accountName')}:</span> {supplier.bankAccount.accountName}</p>}
                    {supplier.bankAccount.accountNumber && <p><span className="text-muted-foreground">{t('purchasing.accountNumber')}:</span> {supplier.bankAccount.accountNumber}</p>}
                    {supplier.bankAccount.branchName && <p><span className="text-muted-foreground">{t('purchasing.bankBranch')}:</span> {supplier.bankAccount.branchName}</p>}
                  </div>
                </div>
              )}

              {/* Notes */}
              {supplier.notes && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-3">
                    {t('common.notes')}
                  </h3>
                  <p className="text-sm">{supplier.notes}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="space-y-3">
              {documents && documents.length > 0 ? (
                documents.map((doc) => (
                  <div
                    key={doc._id}
                    className="flex items-center justify-between p-4 rounded-lg border hover:bg-secondary/50"
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{doc.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {t(`purchasing.docType.${doc.type}`)} • {formatDate(doc.createdAt)}
                        </p>
                      </div>
                    </div>
                    <a
                      href={doc.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline text-sm"
                    >
                      {t('common.download')}
                    </a>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-20" />
                  <p>{t('purchasing.noDocuments')}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'prices' && (
            <div className="space-y-3">
              {priceHistory && priceHistory.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3">{t('purchasing.ingredient')}</th>
                        <th className="text-right py-2 px-3">{t('purchasing.price')}</th>
                        <th className="text-center py-2 px-3">{t('purchasing.change')}</th>
                        <th className="text-right py-2 px-3">{t('purchasing.effectiveFrom')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {priceHistory.map((price) => (
                        <tr key={price._id} className="border-b hover:bg-secondary/50">
                          <td className="py-2 px-3">{price.ingredientId.name}</td>
                          <td className="py-2 px-3 text-right">
                            {formatCurrency(price.unitPrice)}/{price.unit}
                          </td>
                          <td className="py-2 px-3 text-center">
                            {price.priceChangeType === 'increase' && (
                              <span className="text-red-600">+{price.priceChangePercent?.toFixed(1)}%</span>
                            )}
                            {price.priceChangeType === 'decrease' && (
                              <span className="text-green-600">{price.priceChangePercent?.toFixed(1)}%</span>
                            )}
                            {price.priceChangeType === 'new' && (
                              <span className="text-blue-600">{t('purchasing.new')}</span>
                            )}
                            {price.priceChangeType === 'no_change' && (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-right text-muted-foreground">
                            {formatDate(price.effectiveFrom)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <TrendingUp className="h-12 w-12 mx-auto mb-3 opacity-20" />
                  <p>{t('purchasing.noPriceHistory')}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
